import { useDeferredValue, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useFraudAdminUsers, useFraudUserProfile } from '@/lib/api/fraud-queries'
import type { FraudAdminUser } from '@/lib/api/fraud-types'
import { cn } from '@/lib/utils'
import { DecisionChip, ScoreBadge, Spinner } from '../shared'
import { EMPTY, PILL, ROW, TD, TH, fmtDateTime, instrumentCount, moneyFormatter, relativeTime, userDisplayName } from '../format'
import { CardBrand, cardText } from '../card-brand'
import { AccountBlockForm, AdminLiftButton } from './blocks-admin'

/**
 * Find an attendee across every event, then see their whole history: each
 * event's score and decision, cards, blocks at every scope, chargebacks.
 */
export function AdminAttendees({ initialUserId }: { initialUserId?: number }) {
  const [q, setQ] = useState('')
  const deferredQ = useDeferredValue(q)
  const [selected, setSelected] = useState<number | null>(initialUserId ?? null)
  const term = deferredQ.trim()
  const search = useFraudAdminUsers(term, term.length >= 2)
  const results = search.data ?? []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, phone or attendee id" className="w-96" />
        {search.isFetching && <Spinner />}
      </div>

      {term.length >= 2 && (
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 overflow-x-auto">
          {search.error ? (
            <p className="p-6 text-sm text-red-600 dark:text-red-400">Search failed: {search.error.message}</p>
          ) : results.length === 0 ? (
            <p className="p-6 text-sm text-neutral-500 dark:text-neutral-400">{search.isLoading ? 'Searching…' : 'No attendee matches.'}</p>
          ) : (
            <table className="w-full whitespace-nowrap">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-800">
                  <th className={TH}>Attendee</th>
                  <th className={TH}>Contact</th>
                  <th className={TH}>Events</th>
                  <th className={TH}>Last seen</th>
                  <th className={TH}>Blocks</th>
                  <th className={TH}>Chargebacks</th>
                </tr>
              </thead>
              <tbody>
                {results.map((u: FraudAdminUser) => (
                  <tr key={u.id} className={cn(ROW, 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-900', selected === u.id && 'bg-blue-50 dark:bg-blue-900/20')} onClick={() => setSelected(u.id)}>
                    <td className={TD}>
                      <span className="font-medium text-neutral-900 dark:text-white">{userDisplayName(u)}</span>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">#{u.id}{!u.is_active && ' · inactive'}</div>
                    </td>
                    <td className={TD}>{u.email}<div className="text-xs text-neutral-500 dark:text-neutral-400">{u.phone_number ?? EMPTY}</div></td>
                    <td className={TD}>{u.events}</td>
                    <td className={TD}>{u.last_seen ? relativeTime(u.last_seen) : EMPTY}</td>
                    <td className={cn(TD, u.active_blocks && 'text-red-600 dark:text-red-400 font-medium')}>{u.active_blocks}</td>
                    <td className={cn(TD, u.chargebacks && 'text-red-600 dark:text-red-400 font-medium')}>{u.chargebacks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {selected && <AttendeeProfile userId={selected} />}
    </div>
  )
}

function AttendeeProfile({ userId }: { userId: number }) {
  const { data, isLoading, error } = useFraudUserProfile(userId)
  const [showBlock, setShowBlock] = useState(false)
  const [blockingCard, setBlockingCard] = useState<{ instrument_id: string; label: string } | null>(null)

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">Failed to load the attendee: {error.message}</p>
  if (isLoading || !data) return <div className="flex items-center justify-center py-10"><Spinner /></div>
  const activeAccountBlock = data.blocks.find((b) => b.is_active && b.scope === 'account')

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-neutral-900 dark:text-white">{userDisplayName(data.user)}</h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            #{data.user.id} · {data.user.email}{data.user.phone_number ? ` · ${data.user.phone_number}` : ''} · joined {fmtDateTime(data.user.date_joined)}
            {!data.user.is_active && <span className="ml-2 text-red-600">inactive</span>}
          </p>
          {activeAccountBlock && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">Blocked account-wide ({activeAccountBlock.reason.replace(/_/g, ' ')}) since {fmtDateTime(activeAccountBlock.created_at)}</p>
          )}
        </div>
        {!activeAccountBlock && (
          <Button variant="destructive" size="sm" onClick={() => setShowBlock((v) => !v)}>{showBlock ? 'Cancel' : 'Block account-wide'}</Button>
        )}
      </div>

      {showBlock && !activeAccountBlock && (
        <Card>
          <CardHeader><CardTitle className="text-base">Block {userDisplayName(data.user)} at every event</CardTitle></CardHeader>
          <CardContent><AccountBlockForm userId={userId} onDone={() => setShowBlock(false)} /></CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Events</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto px-0">
          {data.events.length === 0 ? <p className="px-6 pb-4 text-sm text-neutral-500">No card activity at any event.</p> : (
            <table className="w-full whitespace-nowrap">
              <thead><tr className="border-b border-neutral-200 dark:border-neutral-800">
                <th className={TH}>Event</th><th className={TH}>Score</th><th className={TH}>Next charge</th><th className={TH}>Card charges</th><th className={TH}>Cards</th><th className={TH}>Balance</th><th className={TH}>Block</th>
              </tr></thead>
              <tbody>
                {data.events.map((row) => {
                  const money = moneyFormatter(row.event.currency)
                  return (
                    <tr key={row.event.id} className={ROW}>
                      <td className={TD}>
                        <Link to="/events/$eventId/fraud/users/$userId" params={{ eventId: String(row.event.id), userId: String(userId) }} className="text-blue-600 dark:text-blue-400 hover:underline">
                          {row.event.name}
                        </Link>
                      </td>
                      <td className={TD}><ScoreBadge score={row.score} /></td>
                      <td className={TD}>
                        <DecisionChip decision={row.decision} />
                        {row.trusted && <div className="mt-1 text-xs text-green-700 dark:text-green-400">trusted</div>}
                      </td>
                      <td className={TD}>{row.reloads.count} · {money(row.reloads.amount)}<div className="text-xs text-neutral-500 dark:text-neutral-400">last {relativeTime(row.reloads.last_at)}</div></td>
                      <td className={TD}>{row.cards.distinct}{row.cards.shared_with_users ? <div className="text-xs text-amber-600 dark:text-amber-400">shared with {row.cards.shared_with_users}</div> : null}</td>
                      <td className={TD}>{row.balance.toLocaleString()}</td>
                      <td className={TD}>{row.block ? <span className={cn(PILL, 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400')}>{row.block.scope}</span> : EMPTY}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Blocks</CardTitle></CardHeader>
          <CardContent className="px-0">
            {data.blocks.length === 0 ? <p className="px-6 pb-4 text-sm text-neutral-500">Never blocked.</p> : (
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {data.blocks.map((b) => (
                  <li key={b.id} className="px-6 py-2.5 text-sm flex flex-wrap items-center gap-3">
                    <span className={cn(PILL, b.is_active ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300')}>
                      {b.is_active ? 'Active' : b.lifted_at ? 'Lifted' : 'Expired'}
                    </span>
                    <span className="text-neutral-900 dark:text-white">
                      {b.kind === 'card' ? `card ${b.card ? cardText(b.card.brand, b.card.last4, b.card.wallet) : b.instrument_id} · ` : ''}
                      {b.scope}{b.scope === 'event' && b.event_name ? ` · ${b.event_name}` : b.scope === 'organization' && b.organization_name ? ` · ${b.organization_name}` : ''} · {b.reason.replace(/_/g, ' ')}{b.kind === 'card' ? '' : b.freeze_spending ? '' : ' · cards only'}
                    </span>
                    <span className="text-xs text-neutral-500">{fmtDateTime(b.created_at)}{b.notes ? ` · ${b.notes}` : ''}{b.lift_notes ? ` · lifted: ${b.lift_notes}` : ''}</span>
                    {b.is_active && <span className="ml-auto"><AdminLiftButton block={b} /></span>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Chargebacks and cards</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            {data.chargebacks.length === 0 ? <p className="text-neutral-500">No chargebacks.</p> : (
              <ul className="space-y-1">
                {data.chargebacks.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-2">
                    <span className={cn(PILL, c.state === 'lost' || c.state === 'accepted' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' : c.state === 'won' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400')}>{c.state}</span>
                    <span>{moneyFormatter(c.currency)(c.amount)} · {c.event_name ?? 'no event'} · {c.reason || 'no reason code'} · {fmtDateTime(c.received_at)}</span>
                  </li>
                ))}
              </ul>
            )}
            {data.cards_on_file.length > 0 && (
              <ul className="space-y-1 pt-2 border-t border-neutral-100 dark:border-neutral-800">
                {data.cards_on_file.map((c) => {
                  const label = cardText(c.brand, c.last4, c.wallet)
                  const blockedHere = data.blocks.some((b) => b.is_active && b.kind === 'card' && b.instrument_id === c.instrument_id)
                  return (
                    <li key={c.instrument_id} className="text-neutral-700 dark:text-neutral-300 flex flex-wrap items-center gap-2">
                      <span>
                        <CardBrand brand={c.brand} last4={c.last4} wallet={c.wallet} />{c.card_type ? ` · ${c.card_type}` : ''} · {c.wallet ? instrumentCount(c.instruments) : c.three_ds_verified_at ? '3DS verified' : 'not 3DS verified'} · added {fmtDateTime(c.created_at)}
                      </span>
                      {blockedHere ? (
                        <span className={cn(PILL, 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400')}>Blocked</span>
                      ) : (
                        <button
                          className="text-xs text-red-600 dark:text-red-400 hover:underline"
                          onClick={() => setBlockingCard(blockingCard?.instrument_id === c.instrument_id ? null : { instrument_id: c.instrument_id, label })}
                        >
                          {blockingCard?.instrument_id === c.instrument_id ? 'Cancel' : 'Block card everywhere'}
                        </button>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
            {blockingCard && (
              <div className="pt-3 border-t border-neutral-100 dark:border-neutral-800">
                <AccountBlockForm instrumentId={blockingCard.instrument_id} cardLabel={blockingCard.label} onDone={() => setBlockingCard(null)} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
